/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { OnboardingStatus } from '@g17eco/types/onboarding';
import { companyTrackerUserRoles, DelegationRoles, UserRoles } from '@constants/user';
import { UserMin, UserPermissions } from '@g17eco/types/user';

const validRoles: string[] = Object.values(UserRoles);
export const getUserRoles = ({ permissions }: { permissions?: UserPermissions[] }) => {
  if (!permissions) {
    return [];
  }

  const roles = permissions.map(p => p.permissions).flat().filter(r => validRoles.includes(r));
  return Array.from(new Set(roles));
};

export const getChecked = (role: UserRoles, selectedRoles: UserRoles[]) => {
  switch (role) {
    case UserRoles.Contributor:
      return selectedRoles.includes(UserRoles.Contributor) && !selectedRoles.includes(UserRoles.Verifier);
    case UserRoles.Verifier:
      return selectedRoles.includes(UserRoles.Verifier) && !selectedRoles.includes(UserRoles.Contributor);
    case UserRoles.ContributorVerifier:
      return selectedRoles.includes(UserRoles.Contributor) && selectedRoles.includes(UserRoles.Verifier);
    default:
      return selectedRoles.includes(role);
  }
}

export const handleRoleRadioChange = (role: UserRoles, action: 'add' | 'remove', prevRoles: UserRoles[]) => {
  if (action === 'remove') {
    const newRoles = prevRoles.filter((prevRole) => prevRole !== role);
    if (newRoles.length === 0) {
      return [UserRoles.Viewer];
    }
    return newRoles;
  }

  const basicRoles = [UserRoles.User, UserRoles.Viewer, UserRoles.Guest];
  if (basicRoles.includes(role)) {
    return [role];
  }
  
  // @Deprecated owner no longer be an option for update
  if (role === UserRoles.Owner) {
    return Array.from(
      new Set([...prevRoles.filter((r) => !basicRoles.includes(r)), UserRoles.Owner, UserRoles.Manager]),
    );
  }

  if (role === UserRoles.Manager) {
    return Array.from(new Set([...prevRoles.filter((r) => !basicRoles.includes(r)), UserRoles.Manager]));
  }

  let newRoles: UserRoles[] = prevRoles.filter((r) => r === UserRoles.Owner || r === UserRoles.Manager);
  if (role === UserRoles.ContributorVerifier) {
    newRoles = [...newRoles, UserRoles.Verifier, UserRoles.Contributor];
  } else {
    newRoles.push(role);
  }

  return Array.from(new Set(newRoles));
}

/**
 * Filter out owner role from permissions for API calls
 * The backend only allows non-owner roles to be updated
 */
export const filterNonOwnerRoles = (roles: UserRoles[]): UserRoles[] => {
  return roles.filter(role => role !== UserRoles.Owner);
}

export const getFullName = (user: Pick<UserMin, 'firstName' | 'surname'>, fallback = '-') => {
  return `${user.firstName ?? ''} ${user.surname ?? ''}`.trim() || fallback;
}

export const getUserFullName = (user?: UserMin) => {
  if (!user) {
    return '';
  }

  return getFullName(user, '');
}

export const getRoleName = (role: UserRoles, maxLength?: number) => {
  const userRole = companyTrackerUserRoles.find(r => r.code === role);
  const name = userRole?.label ?? role;
  if (maxLength && name.length > maxLength) {
    return `${name.substring(0, maxLength)}...`;
  }
  return name;
}

export const isOnboardingUser = (status: OnboardingStatus | undefined) => {
  return status && [OnboardingStatus.Pending, OnboardingStatus.NotStarted].includes(status);
};

export const hasStakeholderRole = (roles: string[]) => {
  return [
    DelegationRoles.UtrvStakeholder,
    DelegationRoles.InitiativeStakeholder,
  ].some((role) => roles.includes(role));
};

export const hasVerifierRole = (roles: string[]) => {
  return [
    DelegationRoles.UtrvVerifier,
    DelegationRoles.InitiativeVerifier,
  ].some((role) => roles.includes(role));
};
